/**
 * Collapses long text blocks to N lines and toggles full copy via a More link.
 */

/** @param {HTMLElement} block */
function shouldApplyReadMore(block) {
  const range = block.dataset.readMoreRange;
  if (range === 'tablet') {
    return window.matchMedia('(min-width: 750px) and (max-width: 1023px)').matches;
  }
  return true;
}

/** @param {HTMLElement} block */
function getReadMoreContentNodes(block) {
  const content = block.querySelector('[data-read-more-content]');
  if (content) return [content];
  return [...block.children].filter((node) => !node.classList.contains('text-read-more__toggle'));
}

/** @param {HTMLElement} block */
function resetReadMoreBlock(block) {
  block.classList.remove('text-read-more--clamp', 'is-expanded');

  const toggle = block.querySelector('.text-read-more__toggle');
  if (toggle) {
    toggle.classList.add('hidden');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.querySelector('.text-read-more__label--more')?.classList.remove('hidden');
    toggle.querySelector('.text-read-more__label--less')?.classList.add('hidden');
  }
}

/** @param {HTMLElement} block */
function setupReadMoreBlock(block) {
  const lines = Number(block.dataset.readMore) || 3;
  block.style.setProperty('--text-read-more-lines', String(lines));

  const toggle = block.querySelector('.text-read-more__toggle');
  if (!toggle) return;

  block.classList.add('text-read-more--clamp');

  requestAnimationFrame(() => {
    if (!isTextTruncated(block)) {
      block.classList.remove('text-read-more--clamp');
      toggle.classList.add('hidden');
      return;
    }

    toggle.classList.remove('hidden');

    if (toggle.dataset.readMoreBound === 'true') return;
    toggle.dataset.readMoreBound = 'true';

    toggle.addEventListener('click', () => {
      const expanded = block.classList.toggle('is-expanded');
      block.classList.toggle('text-read-more--clamp', !expanded);
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.querySelector('.text-read-more__label--more')?.classList.toggle('hidden', expanded);
      toggle.querySelector('.text-read-more__label--less')?.classList.toggle('hidden', !expanded);
    });
  });
}

function initTextReadMore(root = document) {
  root.querySelectorAll('[data-read-more]').forEach((block) => {
    if (!(block instanceof HTMLElement)) return;

    resetReadMoreBlock(block);

    if (!shouldApplyReadMore(block)) {
      block.dataset.readMoreReady = 'idle';
      return;
    }

    block.dataset.readMoreReady = 'true';
    setupReadMoreBlock(block);
  });
}

/**
 * @param {HTMLElement} block
 * @returns {boolean}
 */
function isTextTruncated(block) {
  return getReadMoreContentNodes(block).some((node) => node.scrollHeight > node.clientHeight + 1);
}

function refreshTabletReadMore() {
  document.querySelectorAll('[data-read-more-range="tablet"]').forEach((block) => {
    if (block instanceof HTMLElement) {
      delete block.dataset.readMoreReady;
    }
  });
  initTextReadMore();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initTextReadMore());
} else {
  initTextReadMore();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) {
    initTextReadMore(event.target);
  }
});

let readMoreResizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(readMoreResizeTimer);
  readMoreResizeTimer = window.setTimeout(refreshTabletReadMore, 150);
});
